'use client';

import { useState, useEffect, Suspense } from 'react';
import { useSession } from 'next-auth/react';
import { useSearchParams, useRouter } from 'next/navigation';
import { Sparkles, Download, Loader2, Check, Crown, Star, ChevronDown } from 'lucide-react';
import { MagicalGenerationModal } from './magical-generation-modal';
import { InsufficientCreditsModal } from './insufficient-credits-modal';
import { PurchaseSuccessModal } from './purchase-success-modal';
import { RateLimitModal } from './rate-limit-modal';
import { BooksGallery } from './books-gallery';
import { MagicalStoryModal } from './magical-story-modal';
import AudioBookVideoPlayer from './audio-book/audio-book-video-player';
import MontessoriToggle from './montessori/MontessoriToggle';
import MontessoriFocusSelector from './montessori/MontessoriFocusSelector';
import MontessoriUpsellModal from './montessori/MontessoriUpsellModal';
import type { MontessoriFocus } from '@/lib/montessori';
import Link from 'next/link';
import { shouldHideAgePicker, formatAgeRange, getAgeBucketFromRange } from '@/lib/personalization';

const AGE_BUCKETS = ['0-1', '1-2', '3-4', '5-6', '7-8', '9-10'];
const THEME_KEYWORDS = {
  animals: ['animal', 'dog', 'cat', 'bird', 'bunny', 'rabbit', 'elephant', 'lion', 'tiger', 'bear', 'fox', 'wolf', 'deer', 'monkey', 'giraffe'],
  adventure: ['adventure', 'journey', 'explore', 'quest', 'travel', 'discover', 'expedition'],
  fantasy: ['fantasy', 'magic', 'magical', 'fairy', 'wizard', 'dragon', 'unicorn', 'mythical', 'enchanted'],
  space: ['space', 'astronaut', 'planet', 'star', 'rocket', 'alien', 'galaxy', 'moon', 'cosmos'],
  underwater: ['underwater', 'ocean', 'sea', 'fish', 'mermaid', 'whale', 'dolphin', 'coral', 'submarine'],
  dinosaurs: ['dinosaur', 'dino', 'trex', 't-rex', 'prehistoric', 'jurassic'],
  princess: ['princess', 'prince', 'royal', 'castle', 'crown', 'kingdom', 'palace'],
  superhero: ['superhero', 'hero', 'power', 'super', 'cape', 'rescue', 'brave'],
  nature: ['nature', 'forest', 'tree', 'flower', 'garden', 'plant', 'outdoor', 'mountain'],
  vehicles: ['vehicle', 'car', 'truck', 'train', 'plane', 'boat', 'bus', 'motorcycle'],
  magic: ['magic', 'spell', 'wand', 'potion', 'sorcerer', 'enchanted'],
  forest: ['forest', 'woods', 'tree', 'jungle', 'wilderness'],
  ocean: ['ocean', 'sea', 'beach', 'wave', 'tide'],
  castle: ['castle', 'fortress', 'tower', 'palace', 'kingdom'],
  robot: ['robot', 'mechanical', 'android', 'cyborg', 'machine'],
};

// Auto-generate themes from prompt
function autoGenerateThemes(prompt: string): string[] {
  const lowercasePrompt = prompt.toLowerCase();
  const themes: string[] = [];
  
  for (const [theme, keywords] of Object.entries(THEME_KEYWORDS)) {
    for (const keyword of keywords) {
      if (lowercasePrompt.includes(keyword)) {
        themes.push(`#${theme}`);
        break; // Only add each theme once
      }
    }
  }
  
  // If no themes detected, return a default set
  if (themes.length === 0) {
    return ['#adventure', '#fantasy', '#nature'];
  }
  
  // Limit to max 5 themes
  return themes.slice(0, 5);
}

function ColoringPageContent() {
  const { data: session, status: sessionStatus } = useSession();
  const searchParams = useSearchParams();
  const router = useRouter();
  const [prompt, setPrompt] = useState('');
  const [ageBucket, setAgeBucket] = useState<string | undefined>();
  const [generating, setGenerating] = useState(false);
  const [generationId, setGenerationId] = useState<string | null>(null);
  const [status, setStatus] = useState<any>(null);
  const [credits, setCredits] = useState(0);
  const [subscriptionPlan, setSubscriptionPlan] = useState<string>('NONE');
  const [generationProgress, setGenerationProgress] = useState(0);
  const [generationStep, setGenerationStep] = useState('Preparing your magical story...');
  const [previewImages, setPreviewImages] = useState<string[]>([]);
  const [showInsufficientCreditsModal, setShowInsufficientCreditsModal] = useState(false);
  const [hasScrolledToResults, setHasScrolledToResults] = useState(false);
  const [showPurchaseSuccess, setShowPurchaseSuccess] = useState(false);
  const [generatingIdea, setGeneratingIdea] = useState(false);
  const [purchaseDetails, setPurchaseDetails] = useState<{type: 'credits' | 'subscription'; amount: number} | null>(null);
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [showRateLimitModal, setShowRateLimitModal] = useState(false);
  const [showMagicalStoryModal, setShowMagicalStoryModal] = useState(false);
  const [generatedStoryIdea, setGeneratedStoryIdea] = useState('');
  const [montessoriMode, setMontessoriMode] = useState(false);
  const [montessoriFocus, setMontessoriFocus] = useState<MontessoriFocus>('mixed');
  const [showMontessoriUpsell, setShowMontessoriUpsell] = useState(false);
  const [isSubscriber, setIsSubscriber] = useState(false);
  const [isAgeExpanded, setIsAgeExpanded] = useState(false);
  const [userKidAgeRange, setUserKidAgeRange] = useState<string | null>(null);

  useEffect(() => {
    if (session?.user) {
      fetchUserCredits();
      
      // Check for post-login Montessori redirect
      if (typeof window !== 'undefined') {
        const montessoriRedirect = sessionStorage.getItem('montessori-redirect');
        if (montessoriRedirect && montessoriRedirect === window.location.pathname) {
          sessionStorage.removeItem('montessori-redirect');
          // Show subscription modal after short delay
          setTimeout(() => {
            setShowMontessoriUpsell(true);
          }, 500);
        }
      }
    }
  }, [session]);

  // Check for purchase success from URL params
  useEffect(() => {
    const purchase = searchParams.get('purchase');
    const type = searchParams.get('type');
    const amount = searchParams.get('amount');

    if (purchase === 'success' && type && amount) {
      setPurchaseDetails({
        type: type as 'credits' | 'subscription',
        amount: parseInt(amount, 10)
      });
      setShowPurchaseSuccess(true);
      
      if (session?.user) {
        fetchUserCredits();
      }
      
      router.replace('/coloring', { scroll: false });
    }
  }, [searchParams, session, router]);

  useEffect(() => {
    if (generationId) {
      let pollInterval = 3000;
      let pollCount = 0;
      
      const poll = () => {
        checkStatus();
        pollCount++;
        
        if (pollCount > 20) pollInterval = 5000;
        if (pollCount > 60) pollInterval = 8000;
      };
      
      poll();
      
      let timeoutId: NodeJS.Timeout;
      const scheduleNext = () => {
        timeoutId = setTimeout(() => {
          poll();
          scheduleNext();
        }, pollInterval);
      };
      
      scheduleNext();
      
      return () => clearTimeout(timeoutId);
    }
  }, [generationId]);

  // Realistic progress simulation
  useEffect(() => {
    if (generating && !status?.pdfUrl) {
      setGenerationProgress(0);
      setGenerationStep('⚡ Generating all pages in parallel...');
      setPreviewImages([]);

      const startTime = Date.now();
      const estimatedDuration = status?.pages === 13 ? 540000 : 150000;
      
      const progressInterval = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const timeBasedProgress = (elapsed / estimatedDuration) * 100;
        
        setGenerationProgress((prev) => {
          const targetProgress = Math.min(timeBasedProgress, 92);
          const increment = (targetProgress - prev) * 0.15;
          const newProgress = Math.min(prev + increment + Math.random() * 0.5, 92);
          
          if (newProgress >= 5 && newProgress < 25) {
            setGenerationStep('Creating all coloring pages at once...');
          } else if (newProgress >= 25 && newProgress < 50) {
            setGenerationStep('Adding magical details to each page...');
          } else if (newProgress >= 50 && newProgress < 70) {
            setGenerationStep('Perfecting coloring-friendly lines...');
          } else if (newProgress >= 70 && newProgress < 85) {
            setGenerationStep('Assembling your PDF...');
          } else if (newProgress >= 85) {
            setGenerationStep('Almost ready!');
          }
          
          return newProgress;
        });
      }, 800);

      return () => clearInterval(progressInterval);
    }
  }, [generating, status?.pdfUrl, status?.pages]);

  // Update progress when generation completes
  useEffect(() => {
    if (status?.status === 'completed') {
      setGenerationProgress(100);
      setGenerationStep('🎉 Your magical book is ready!');
      
      if (status.previewUrls) {
        setPreviewImages(status.previewUrls);
      }

      setTimeout(() => {
        setGenerating(false);
        
        if (!hasScrolledToResults) {
          setTimeout(() => {
            const resultsSection = document.getElementById('book-results');
            if (resultsSection) {
              const navbarHeight = 80;
              const elementPosition = resultsSection.getBoundingClientRect().top + window.pageYOffset;
              const offsetPosition = elementPosition - navbarHeight;
              window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
              });
              setHasScrolledToResults(true);
            }
          }, 200);
        }
      }, 800);
    } else if (status?.status === 'failed') {
      setGenerationProgress(0);
      setGenerationStep('Something went wrong. Please try again.');
      setTimeout(() => {
        setGenerating(false);
      }, 2000);
    }
  }, [status, hasScrolledToResults]);

  async function fetchUserCredits() {
    try {
      const res = await fetch('/api/account');
      if (res.ok) {
        const data = await res.json();
        setCredits(data.credits || 0);
        setSubscriptionPlan(data.subscriptionPlan || 'NONE');
        setIsSubscriber(data.isSubscriber || false);
        setUserKidAgeRange(data.kidAgeRange || null);
        
        // Auto-set age bucket from user's kid profile if available
        if (data.kidAgeRange && !ageBucket) {
          const bucket = getAgeBucketFromRange(data.kidAgeRange);
          if (bucket && AGE_BUCKETS.includes(bucket)) {
            setAgeBucket(bucket);
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch credits:', error);
    }
  }

  async function checkStatus() {
    if (!generationId) return;
    
    try {
      const res = await fetch(`/api/generate/status?id=${generationId}`);
      if (res.ok) {
        const data = await res.json();
        setStatus(data);
        
        if (data.status === 'completed' || data.status === 'failed') {
          setGenerating(false);
        }
      }
    } catch (error) {
      console.error('Status check failed:', error);
    }
  }

  async function handleGenerateIdea() {
    setGeneratingIdea(true);
    try {
      const res = await fetch('/api/generate-idea', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      const data = await res.json();

      if (res.ok && data.idea) {
        setPrompt(data.idea);
      } else {
        alert('Failed to generate idea. Please try again.');
      }
    } catch (error) {
      console.error('Idea generation error:', error);
      alert('Failed to generate idea. Please try again.');
    } finally {
      setGeneratingIdea(false);
    }
  }

  async function handleGenerate() {
    if (!prompt.trim()) {
      await generateAndShowMagicalStory();
      return;
    }

    if (!session) {
      return handleGeneratePreview();
    }
    
    if (credits > 0 || session.user?.isSubscriber) {
      return handleGenerateFull();
    }
    
    setShowInsufficientCreditsModal(true);
  }

  async function generateAndShowMagicalStory() {
    setGeneratingIdea(true);
    try {
      const res = await fetch('/api/generate-idea', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });

      const data = await res.json();

      if (res.ok && data.idea) {
        setGeneratedStoryIdea(data.idea);
        setShowMagicalStoryModal(true);
      } else {
        alert('Failed to generate a magical story. Please try again!');
      }
    } catch (error) {
      console.error('Magical story generation error:', error);
      alert('Failed to generate a magical story. Please try again!');
    } finally {
      setGeneratingIdea(false);
    }
  }

  async function handleProceedWithGeneratedStory() {
    const storyToGenerate = generatedStoryIdea;
    setPrompt(storyToGenerate);
    setShowMagicalStoryModal(false);
    
    await new Promise(resolve => setTimeout(resolve, 300));
    
    if (!session) {
      await generateWithPrompt(storyToGenerate, false);
      return;
    }
    
    if (credits > 0 || session.user?.isSubscriber) {
      await generateWithPrompt(storyToGenerate, true);
      return;
    }
    
    setShowInsufficientCreditsModal(true);
  }

  function handleCancelGeneratedStory() {
    setShowMagicalStoryModal(false);
    setGeneratedStoryIdea('');
  }

  async function generateWithPrompt(promptText: string, isFullBook: boolean) {
    if (!promptText.trim()) return;

    setGenerating(true);
    setStatus(null);
    setHasScrolledToResults(false);

    try {
      const endpoint = isFullBook ? '/api/generate/full' : '/api/generate/preview';
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          prompt: promptText, 
          ageBucket, 
          hashtags: autoGenerateThemes(promptText),
          montessoriMode,
          montessoriFocus
        }),
      });

      if (!res.ok) {
        const error = await res.json();
        setGenerating(false);
        
        if (error.error === 'rate_limit') {
          setIsRateLimited(true);
          setShowRateLimitModal(true);
        } else if (error.error && error.error.toLowerCase().includes('insufficient credits')) {
          setShowInsufficientCreditsModal(true);
        } else if (error.error === 'no_credits') {
          setShowInsufficientCreditsModal(true);
        } else {
          alert(error.message || error.error || 'Failed to generate book');
        }
        return;
      }

      const data = await res.json();
      setGenerationId(data.generationId);
      setIsRateLimited(false);
      
      if (isFullBook) {
        fetchUserCredits();
      }
    } catch (error) {
      console.error('Generation failed:', error);
      alert('Failed to generate book');
      setGenerating(false);
    }
  }

  async function handleGeneratePreview() {
    if (!prompt.trim()) return;

    setGenerating(true);
    setStatus(null);
    setHasScrolledToResults(false);

    try {
      const res = await fetch('/api/generate/preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          prompt, 
          ageBucket, 
          hashtags: autoGenerateThemes(prompt),
          montessoriMode,
          montessoriFocus
        }),
      });

      if (!res.ok) {
        const error = await res.json();
        setGenerating(false);
        
        if (error.error === 'rate_limit') {
          setIsRateLimited(true);
          setShowRateLimitModal(true);
        } else if (error.error === 'no_credits') {
          setShowInsufficientCreditsModal(true);
        } else {
          alert(error.message || error.error || 'Failed to generate preview');
        }
        return;
      }

      const data = await res.json();
      setGenerationId(data.generationId);
      setIsRateLimited(false);
    } catch (error) {
      console.error('Preview generation failed:', error);
      alert('Failed to generate preview');
      setGenerating(false);
    }
  }

  async function handleGenerateFull() {
    if (!session) {
      alert('Please sign in to generate a full book');
      return;
    }

    if (!prompt.trim()) return;

    setGenerating(true);
    setStatus(null);
    setHasScrolledToResults(false);

    try {
      const res = await fetch('/api/generate/full', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          prompt, 
          ageBucket, 
          hashtags: autoGenerateThemes(prompt),
          montessoriMode,
          montessoriFocus
        }),
      });

      if (!res.ok) {
        const error = await res.json();
        
        if (error.error && error.error.toLowerCase().includes('insufficient credits')) {
          setGenerating(false);
          setShowInsufficientCreditsModal(true);
          return;
        }
        
        alert(error.error || 'Failed to generate full book');
        setGenerating(false);
        return;
      }

      const data = await res.json();
      setGenerationId(data.generationId);
      fetchUserCredits();
    } catch (error) {
      console.error('Full book generation failed:', error);
      alert('Failed to generate full book');
      setGenerating(false);
    }
  }


  async function handlePurchase(packageType: string) {
    if (!session) {
      sessionStorage.setItem('pendingPurchase', JSON.stringify({ 
        type: 'credits',
        packageType 
      }));
      // Redirect to login page with callback
      const callbackUrl = encodeURIComponent('/coloring');
      router.push(`/login?callbackUrl=${callbackUrl}`);
      return;
    }

    try {
      const res = await fetch('/api/checkout/credits', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ packageType }),
      });

      if (!res.ok) throw new Error('Checkout failed');

      const data = await res.json();
      window.location.href = data.url;
    } catch (error) {
      console.error('Purchase error:', error);
      alert('Failed to start checkout. Please try again.');
    }
  }

  async function handleSubscribe(plan: 'amaya_plus' | 'amaya_pro' = 'amaya_plus') {
    if (!session) {
      sessionStorage.setItem('pendingPurchase', JSON.stringify({ 
        type: 'subscription',
        plan
      }));
      // Redirect to login page with callback
      const callbackUrl = encodeURIComponent('/coloring');
      router.push(`/login?callbackUrl=${callbackUrl}`);
      return;
    }

    try {
      const res = await fetch('/api/checkout/subscription', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ plan: 'amaya_plus' }),
      });

      if (!res.ok) throw new Error('Subscription failed');

      const data = await res.json();
      window.location.href = data.url;
    } catch (error) {
      console.error('Subscribe error:', error);
      alert('Failed to start subscription. Please try again.');
    }
  }

  function scrollToCreate() {
    document.getElementById('create-section')?.scrollIntoView({ behavior: 'smooth' });
  }

  return (
    <>
      <MagicalGenerationModal
        isGenerating={generating}
        progress={generationProgress}
        currentStep={generationStep}
        previewImages={previewImages}
      />

      <MagicalStoryModal
        isOpen={showMagicalStoryModal}
        storyIdea={generatedStoryIdea}
        onProceed={handleProceedWithGeneratedStory}
        onCancel={handleCancelGeneratedStory}
      />

      <RateLimitModal
        isOpen={showRateLimitModal}
        onClose={() => setShowRateLimitModal(false)}
        isLoggedIn={!!session}
      />

      <InsufficientCreditsModal
        isOpen={showInsufficientCreditsModal}
        onClose={() => setShowInsufficientCreditsModal(false)}
        onPurchase={(packageType) => {
          setShowInsufficientCreditsModal(false);
          handlePurchase(packageType);
        }}
        onSubscribe={(plan) => {
          setShowInsufficientCreditsModal(false);
          handleSubscribe(plan || 'amaya_plus');
        }}
      />

      {purchaseDetails && (
        <PurchaseSuccessModal
          isOpen={showPurchaseSuccess}
          onClose={() => {
            setShowPurchaseSuccess(false);
            setPurchaseDetails(null);
          }}
          purchaseType={purchaseDetails.type}
          amount={purchaseDetails.amount}
        />
      )}

      <div style={{ 
        minHeight: '100vh', 
        background: 'linear-gradient(180deg, #FFF9F5 0%, #FFF5F0 30%, #FFEFE8 60%, #FFE8E0 100%)',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Premium Decorative Background Elements - Coloring Theme */}
        
        {/* Top Left - Paint Palette */}
        <div style={{
          position: 'absolute',
          top: '8%',
          left: '5%',
          width: '110px',
          height: '110px',
          opacity: 0.08,
          animation: 'float 8s ease-in-out infinite',
        }}>
          <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
            <circle cx="50" cy="50" r="45" fill="#C5B4FF" />
            <circle cx="30" cy="35" r="8" fill="white" opacity="0.8" />
            <circle cx="70" cy="35" r="8" fill="white" opacity="0.8" />
            <circle cx="50" cy="55" r="8" fill="white" opacity="0.8" />
            <circle cx="35" cy="65" r="8" fill="white" opacity="0.8" />
            <circle cx="65" cy="65" r="8" fill="white" opacity="0.8" />
          </svg>
        </div>
        
        {/* Top Right - Crayon */}
        <div style={{
          position: 'absolute',
          top: '12%',
          right: '8%',
          width: '95px',
          height: '95px',
          opacity: 0.08,
          animation: 'float 7s ease-in-out infinite 1.5s',
          transform: 'rotate(45deg)',
        }}>
          <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
            <rect x="25" y="10" width="50" height="80" rx="5" fill="#F4A4C4" />
            <path d="M35 10 L65 10 L50 0 Z" fill="#FFB5A7" />
            <rect x="25" y="10" width="50" height="15" fill="#FFB5A7" opacity="0.6" />
          </svg>
        </div>
        
        {/* Middle Left - Star */}
        <div style={{
          position: 'absolute',
          top: '45%',
          left: '3%',
          width: '100px',
          height: '100px',
          opacity: 0.08,
          animation: 'float 9s ease-in-out infinite 2s',
        }}>
          <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M50 10 L60 40 L90 45 L65 65 L72 95 L50 80 L28 95 L35 65 L10 45 L40 40 Z" fill="#A8D8E6" />
          </svg>
        </div>
        
        {/* Bottom Right - Butterfly */}
        <div style={{
          position: 'absolute',
          bottom: '15%',
          right: '5%',
          width: '120px',
          height: '120px',
          opacity: 0.08,
          animation: 'float 10s ease-in-out infinite 1s',
        }}>
          <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
            <ellipse cx="25" cy="40" rx="20" ry="25" fill="#D8B5E8" />
            <ellipse cx="75" cy="40" rx="20" ry="25" fill="#D8B5E8" />
            <ellipse cx="25" cy="70" rx="15" ry="20" fill="#D8B5E8" opacity="0.7" />
            <ellipse cx="75" cy="70" rx="15" ry="20" fill="#D8B5E8" opacity="0.7" />
            <rect x="48" y="20" width="4" height="60" rx="2" fill="#B5F2E0" />
          </svg>
        </div>
        
        {/* Bottom Left - Rainbow Arc */}
        <div style={{
          position: 'absolute',
          bottom: '25%',
          left: '8%',
          width: '110px',
          height: '110px',
          opacity: 0.08,
          animation: 'float 7.5s ease-in-out infinite 0.5s',
        }}>
          <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 80 Q 50 20, 90 80" stroke="#FFB5A7" strokeWidth="6" fill="none" />
            <path d="M15 80 Q 50 28, 85 80" stroke="#F4A4C4" strokeWidth="6" fill="none" />
            <path d="M20 80 Q 50 36, 80 80" stroke="#D8B5E8" strokeWidth="6" fill="none" />
          </svg>
        </div>
        
        {/* Video Hero Section */}
        <div style={{ 
          maxWidth: '1000px', 
          margin: '0 auto',
          padding: '2rem 1.5rem 1rem',
          position: 'relative',
          zIndex: 1
        }}
        className="hero-video-section"
        >
          <div style={{ position: 'relative' }}>
            <AudioBookVideoPlayer 
              videoSrc={session 
                ? "https://pub-c66b80290e37412f86676f07b6cbcd28.r2.dev/signin.mp4"
                : "https://pub-c66b80290e37412f86676f07b6cbcd28.r2.dev/amaya-stories-ai-book-generation.mp4"
              }
              posterSrc="https://pub-c66b80290e37412f86676f07b6cbcd28.r2.dev/Amaya%20Story.webp"
            />
            
            {/* Text overlay on video (hidden on mobile to avoid covering controls) */}
            <div style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              background: 'linear-gradient(to bottom, rgba(255,245,235,0.5) 0%, rgba(255,245,235,0.3) 50%, transparent 100%)',
              pointerEvents: 'none',
              padding: '2rem',
            }}
            className="hero-video-overlay"
            >
              <h1 style={{
                fontSize: 'clamp(1.75rem, 5vw, 3rem)',
                fontWeight: 800,
                marginBottom: '0.75rem',
                background: 'linear-gradient(135deg, #D64481 0%, #5E9FBE 50%, #9570BC 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textAlign: 'center',
                lineHeight: 1.2,
                filter: 'brightness(0.95)',
              }}>
                Create Coloring Book
              </h1>
              <p style={{
                fontSize: 'clamp(1rem, 2.5vw, 1.25rem)',
                color: '#333',
                textAlign: 'center',
                marginBottom: 0,
                maxWidth: '600px',
                fontWeight: 700,
                textShadow: '0 2px 4px rgba(255,255,255,0.6)',
              }}>
                Education • Montessori Inspired
              </p>
            </div>
          </div>
        </div>

        {/* 🎨 2026 Award-Winning PDF Feature Badge */}
        <div style={{
          maxWidth: '1000px',
          margin: '1.5rem auto 1.5rem',
          padding: '0 1.5rem',
          display: 'flex',
          justifyContent: 'center',
          position: 'relative',
          zIndex: 2,
        }}
        className="pdf-badge-container"
        >
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '1rem',
            padding: '1.25rem 2rem',
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.9) 100%)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            borderRadius: '24px',
            border: '2px solid rgba(197, 180, 255, 0.3)',
            boxShadow: '0 12px 48px rgba(197, 180, 255, 0.25), inset 0 2px 12px rgba(255, 255, 255, 0.6), 0 0 0 1px rgba(255, 255, 255, 0.5)',
            position: 'relative',
            overflow: 'hidden',
          }}
          className="pdf-badge"
          >
            {/* Shimmer animation overlay */}
            <div style={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(90deg, transparent, rgba(197, 180, 255, 0.2), transparent)',
              animation: 'shimmer 3s ease-in-out infinite',
              pointerEvents: 'none',
            }} />

            {/* PDF Icon with gradient */}
            <div style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '48px',
              height: '48px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #C5B4FF 0%, #B5F2E0 100%)',
              boxShadow: '0 4px 16px rgba(197, 180, 255, 0.4)',
            }}>
              <svg 
                width="24" 
                height="24" 
                viewBox="0 0 24 24" 
                fill="none" 
                xmlns="http://www.w3.org/2000/svg"
                style={{ position: 'relative', zIndex: 1 }}
              >
                <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" fill="white" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M14 2V8H20" fill="white" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M8 13H16" stroke="#7C3AED" strokeWidth="2" strokeLinecap="round"/>
                <path d="M8 17H16" stroke="#7C3AED" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>

            {/* Text content */}
            <div style={{ position: 'relative', zIndex: 1 }}>
              <div style={{
                fontSize: '1.125rem',
                fontWeight: 800,
                background: 'linear-gradient(135deg, #7C3AED 0%, #10b981 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                marginBottom: '0.125rem',
                letterSpacing: '-0.01em',
              }}
              className="pdf-badge-title"
              >
                PDF Ready
              </div>
              <div style={{
                fontSize: '0.875rem',
                color: '#64748b',
                fontWeight: 600,
              }}
              className="pdf-badge-subtitle"
              >
                High-quality coloring pages for instant printing
              </div>
            </div>

            {/* Printer icon badge */}
            <div style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              background: 'rgba(16, 185, 129, 0.1)',
              border: '1.5px solid rgba(16, 185, 129, 0.2)',
            }}>
              <svg 
                width="20" 
                height="20" 
                viewBox="0 0 24 24" 
                fill="none" 
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M6 9V2H18V9" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M6 18H4C3.46957 18 2.96086 17.7893 2.58579 17.4142C2.21071 17.0391 2 16.5304 2 16V11C2 10.4696 2.21071 9.96086 2.58579 9.58579C2.96086 9.21071 3.46957 9 4 9H20C20.5304 9 21.0391 9.21071 21.4142 9.58579C21.7893 9.96086 22 10.4696 22 11V16C22 16.5304 21.7893 17.0391 21.4142 17.4142C21.0391 17.7893 20.5304 18 20 18H18" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M18 14H6V22H18V14Z" stroke="#10b981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>
        </div>


        {/* Create Section */}
        <div id="create-section" className="container create-section" style={{ 
          maxWidth: '1000px', 
          margin: '0 auto', 
          padding: '1rem 1.5rem 2rem',
          scrollMarginTop: '80px'
        }}>
          <div style={{
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(20px)',
            borderRadius: 'var(--r-2xl, 28px)',
            padding: 'clamp(1.5rem, 3vw, 2.5rem)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
            border: '1px solid rgba(255, 255, 255, 0.5)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
              <div style={{ flex: 1 }}>
                <p style={{ 
                  fontSize: '0.875rem', 
                  color: 'var(--color-text-secondary)', 
                  margin: 0,
                  fontWeight: 500
                }}>
                  {session ? '1 credit per coloring book' : 'Try free demo book'}
                </p>
              </div>
              <button
                onClick={handleGenerateIdea}
                disabled={generatingIdea || generating}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.5rem 1rem',
                  background: 'linear-gradient(135deg, #8B5CF6 0%, #A78BFA 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '50px',
                  cursor: generatingIdea || generating ? 'not-allowed' : 'pointer',
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  transition: 'all 0.3s ease',
                  boxShadow: '0 2px 8px rgba(139, 92, 246, 0.3)',
                }}
              >
                {generatingIdea ? (
                  <>
                    <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} />
                    <span>Thinking...</span>
                  </>
                ) : (
                  <>
                    <Sparkles size={16} />
                    <span>Inspire Me</span>
                  </>
                )}
              </button>
            </div>

            <div style={{ position: 'relative', marginBottom: '1.5rem' }}>
              <textarea
                placeholder="Example: A curious bunny goes on an adventure to find the rainbow's end and discovers a magical garden..."
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                disabled={generating}
                style={{ 
                  width: '100%',
                  minHeight: '180px',
                  padding: '1.25rem',
                  fontSize: '1.0625rem',
                  lineHeight: 1.7,
                  borderRadius: 'var(--r-xl, 20px)',
                  border: '2px solid #e0d5f0',
                  outline: 'none',
                  resize: 'vertical',
                  fontFamily: 'inherit',
                  transition: 'all 0.2s ease',
                }}
                onFocus={(e) => {
                  e.currentTarget.style.borderColor = '#C5B4FF';
                  e.currentTarget.style.boxShadow = '0 0 0 4px rgba(197, 180, 255, 0.15)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.borderColor = '#e0d5f0';
                  e.currentTarget.style.boxShadow = 'none';
                }}
                aria-label="Story idea"
              />
            </div>

            <h4 style={{ fontSize: '1.125rem', marginBottom: '1rem', fontWeight: 700 }}>Age Range (optional)</h4>
            
            {shouldHideAgePicker(userKidAgeRange) ? (
              // Show "Using age: X-Y (change)" if age is set in user profile
              <div style={{
                padding: '1rem',
                background: 'linear-gradient(135deg, rgba(168, 216, 230, 0.15) 0%, rgba(184, 226, 240, 0.15) 100%)',
                border: '2px solid #A8D8E6',
                borderRadius: '12px',
                marginBottom: '1.5rem',
              }}>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  flexWrap: 'wrap',
                  gap: '0.75rem',
                }}>
                  <div style={{
                    fontSize: '0.9375rem',
                    fontWeight: 600,
                    color: '#333'
                  }}>
                    Using age: <strong style={{ color: '#0284C7' }}>{formatAgeRange(userKidAgeRange)}</strong> years
                  </div>
                  <Link
                    href="/account"
                    style={{
                      fontSize: '0.875rem',
                      color: '#0284C7',
                      textDecoration: 'underline',
                      fontWeight: 600,
                      cursor: 'pointer',
                    }}
                  >
                    Change in settings
                  </Link>
                </div>
              </div>
            ) : (
              <>
                {/* Mobile: Compact Collapsible Header */}
                <div
                  className="age-range-mobile-header"
                  onClick={() => setIsAgeExpanded(!isAgeExpanded)}
                  style={{
                    display: 'none', // Will be shown on mobile via CSS
                    width: '100%',
                    padding: '0.875rem 1rem',
                    background: ageBucket 
                      ? 'linear-gradient(135deg, #C5B4FF 0%, #B5F2E0 100%)' 
                      : 'rgba(255, 255, 255, 0.9)',
                    color: ageBucket ? '#1a1a1a' : '#64748b',
                    border: ageBucket 
                      ? '2px solid rgba(197, 180, 255, 0.4)' 
                      : '2px solid #e0d5f0',
                    borderRadius: '12px',
                    cursor: 'pointer',
                    fontWeight: ageBucket ? 800 : 600,
                    fontSize: '0.9375rem',
                    transition: 'all 0.3s ease',
                    boxShadow: ageBucket 
                      ? '0 4px 12px rgba(197, 180, 255, 0.3)' 
                      : '0 2px 8px rgba(0, 0, 0, 0.06)',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '0.75rem',
                    minHeight: '48px',
                  }}
                >
                  <span>{ageBucket ? `${ageBucket} years` : 'Select Age Range'}</span>
                  <ChevronDown 
                    size={20} 
                    style={{
                      transition: 'transform 0.3s ease',
                      transform: isAgeExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    }}
                  />
                </div>
                {/* Mobile: Expandable Age Buttons */}
                <div
                  className="age-range-mobile-expanded"
                  style={{
                    display: 'none', // Will be shown on mobile via CSS
                    maxHeight: isAgeExpanded ? '300px' : '0',
                    overflow: 'hidden',
                    transition: 'max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    marginBottom: '1.5rem',
                  }}
                >
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '0.75rem',
                paddingTop: '1rem',
              }}>
                {AGE_BUCKETS.map((age) => (
                  <button
                    key={age}
                    onClick={(e) => {
                      e.stopPropagation();
                      setAgeBucket(ageBucket === age ? undefined : age);
                      setIsAgeExpanded(false);
                    }}
                    disabled={generating}
                    style={{
                      padding: '0.875rem',
                      background: ageBucket === age 
                        ? 'linear-gradient(135deg, #C5B4FF 0%, #B5F2E0 100%)' 
                        : 'rgba(255, 255, 255, 0.9)',
                      color: ageBucket === age ? '#1a1a1a' : '#64748b',
                      border: ageBucket === age 
                        ? '2px solid rgba(197, 180, 255, 0.4)' 
                        : '2px solid #e0d5f0',
                      borderRadius: '12px',
                      cursor: generating ? 'not-allowed' : 'pointer',
                      fontWeight: ageBucket === age ? 800 : 600,
                      fontSize: '0.9375rem',
                      transition: 'all 0.3s ease',
                      boxShadow: ageBucket === age 
                        ? '0 4px 12px rgba(197, 180, 255, 0.3)' 
                        : '0 2px 8px rgba(0, 0, 0, 0.06)',
                      minHeight: '48px',
                    }}
                  >
                    {age} years
                  </button>
                ))}
              </div>
            </div>

            {/* Desktop: Inline Buttons */}
            <div 
              className="age-range-desktop-buttons"
              style={{ 
                display: 'flex',
                flexWrap: 'wrap',
                gap: '0.625rem', 
                marginBottom: '1.5rem',
              }}
            >
              {AGE_BUCKETS.map((age) => (
                <button
                  key={age}
                  onClick={() => setAgeBucket(ageBucket === age ? undefined : age)}
                  disabled={generating}
                  style={{
                    padding: '0.625rem 1.125rem',
                    background: ageBucket === age 
                      ? 'linear-gradient(135deg, #C5B4FF 0%, #B5F2E0 100%)' 
                      : 'rgba(255, 255, 255, 0.9)',
                    color: ageBucket === age ? '#1a1a1a' : '#64748b',
                    border: ageBucket === age 
                      ? '2px solid rgba(197, 180, 255, 0.4)' 
                      : '2px solid #e0d5f0',
                    borderRadius: '12px',
                    cursor: generating ? 'not-allowed' : 'pointer',
                    fontWeight: ageBucket === age ? 800 : 600,
                    fontSize: '0.9375rem',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow: ageBucket === age 
                      ? '0 6px 20px rgba(197, 180, 255, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.5)' 
                      : '0 2px 8px rgba(0, 0, 0, 0.06)',
                    backdropFilter: 'blur(10px)',
                    WebkitTapHighlightColor: 'transparent',
                    position: 'relative',
                    overflow: 'hidden',
                    minWidth: 'fit-content',
                  }}
                  onMouseEnter={(e) => {
                    if (!generating && ageBucket !== age) {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 6px 20px rgba(197, 180, 255, 0.2)';
                      e.currentTarget.style.borderColor = 'rgba(197, 180, 255, 0.5)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!generating) {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = ageBucket === age 
                        ? '0 6px 20px rgba(197, 180, 255, 0.35), inset 0 1px 0 rgba(255, 255, 255, 0.5)' 
                        : '0 2px 8px rgba(0, 0, 0, 0.06)';
                      e.currentTarget.style.borderColor = ageBucket === age 
                        ? 'rgba(197, 180, 255, 0.4)' 
                        : '#e0d5f0';
                    }
                  }}
                >
                  {ageBucket === age && (
                    <div style={{
                      position: 'absolute',
                      inset: 0,
                      background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.3), transparent)',
                      pointerEvents: 'none',
                    }} />
                  )}
                  <span style={{ position: 'relative', zIndex: 1 }}>{age} years</span>
                </button>
              ))}
            </div>
              </>
            )}

            {/* Montessori Mode Section */}
            <div style={{ marginBottom: '2rem' }}>
              <MontessoriToggle
                enabled={montessoriMode}
                isSubscriber={isSubscriber}
                isSignedIn={sessionStatus === 'authenticated' && !!session?.user}
                onChange={setMontessoriMode}
                onUpsell={() => setShowMontessoriUpsell(true)}
              />
              
              {montessoriMode && (
                <div style={{ marginTop: '1rem' }}>
                  <MontessoriFocusSelector
                    value={montessoriFocus}
                    onChange={setMontessoriFocus}
                    disabled={generating}
                  />
                </div>
              )}
            </div>

            <MontessoriUpsellModal
              isOpen={showMontessoriUpsell}
              onClose={() => setShowMontessoriUpsell(false)}
              isSignedIn={sessionStatus === 'authenticated' && !!session?.user}
            />

            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '1rem' }}>
              <button
                onClick={handleGenerate}
                disabled={generating || generatingIdea}
                style={{
                  width: '100%',
                  maxWidth: '400px',
                  padding: '1.25rem 2.5rem',
                  background: generating || generatingIdea ? '#e0d5f0' : 'linear-gradient(135deg, #A8D8E6 0%, #C9B6E4 100%)',
                  color: generating || generatingIdea ? '#888' : 'white',
                  border: 'none',
                  borderRadius: '60px',
                  fontSize: '1.125rem',
                  fontWeight: 700,
                  cursor: generating || generatingIdea ? 'not-allowed' : 'pointer',
                  boxShadow: generating || generatingIdea ? 'none' : '0 8px 24px rgba(168, 216, 230, 0.4)',
                  transition: 'all 0.3s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.75rem',
                }}
                onMouseEnter={(e) => {
                  if (!generating && !generatingIdea) {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 12px 32px rgba(168, 216, 230, 0.5)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = generating || generatingIdea ? 'none' : '0 8px 24px rgba(168, 216, 230, 0.4)';
                }}
              >
                {generatingIdea ? (
                  <>
                    <Loader2 size={22} style={{ animation: 'spin 1s linear infinite' }} />
                    <span>Generating Story Idea...</span>
                  </>
                ) : generating && !status?.pdfUrl ? (
                  <>
                    <Loader2 size={22} style={{ animation: 'spin 1s linear infinite' }} />
                    <span>Creating Your Magic Book...</span>
                  </>
                ) : (
                  <>
                    <Sparkles size={22} />
                    <span>
                      {!prompt.trim() 
                        ? 'Generate Story Idea'
                        : session 
                          ? 'Create Your Coloring Book' 
                          : 'Create Your Free Magic Book'}
                    </span>
                  </>
                )}
              </button>
            </div>

            {!session && (
              <p style={{ marginTop: '1rem', fontSize: '0.9375rem', color: 'var(--color-text-secondary)', textAlign: 'center', fontWeight: 500 }}>
                Free 3-page demo • No credit card • Sign in to save & unlock more
              </p>
            )}
            {session?.user?.isSubscriber && (
              <p style={{ marginTop: '1rem', fontSize: '0.9375rem', color: 'var(--color-text-secondary)', textAlign: 'center', fontWeight: 500 }}>
                Unlimited subscriber • Create as many books as you want!<br />
                <span style={{ fontSize: '0.875rem', opacity: 0.8 }}>Generation takes approximately 2-3 minutes</span>
              </p>
            )}
            {!session?.user?.isSubscriber && session && credits > 0 && (
              <p style={{ marginTop: '1rem', fontSize: '0.9375rem', color: 'var(--color-text-secondary)', textAlign: 'center', fontWeight: 500 }}>
                Generate a full 13-page coloring book • 1 credit will be used<br />
                <span style={{ fontSize: '0.875rem', opacity: 0.8 }}>Generation takes approximately 2-3 minutes</span>
              </p>
            )}
            {session && credits === 0 && !session.user?.isSubscriber && (
              <p style={{ marginTop: '1rem', fontSize: '0.9375rem', color: 'var(--color-text-secondary)', textAlign: 'center', fontWeight: 500 }}>
                Purchase credits to create your full coloring book
              </p>
            )}
          </div>

          {status && (
            <div id="book-results" style={{ 
              marginTop: '2rem', 
              scrollMarginTop: '100px',
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px)',
              borderRadius: 'var(--r-2xl, 28px)',
              padding: 'clamp(1.5rem, 3vw, 2.5rem)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255, 255, 255, 0.8)',
              border: '1px solid rgba(255, 255, 255, 0.5)',
            }}>
              <h3 style={{ marginBottom: '1rem' }}>Ta-da! Amaya finished your storybook!</h3>
              
              {status.status === 'processing' && (
                <div style={{ textAlign: 'center', padding: '2rem' }}>
                  <Loader2 size={48} style={{ margin: '0 auto', animation: 'spin 1s linear infinite' }} />
                  <p style={{ marginTop: '1rem' }}>
                    Creating your magical book... This may take a few minutes.
                  </p>
                </div>
              )}

              {status.status === 'completed' && (
                <>
                  {status.previewUrls && status.previewUrls.length > 0 && (
                    <>
                      {/* Cover Image */}
                      <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
                        <div style={{ 
                          display: 'inline-block',
                          position: 'relative',
                          maxWidth: '500px',
                          width: '100%'
                        }}>
                          <div style={{
                            position: 'absolute',
                            top: '-1rem',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            background: 'linear-gradient(135deg, #A8D8E6, #C9B6E4)',
                            color: 'white',
                            padding: '0.5rem 1.5rem',
                            borderRadius: '50px',
                            fontSize: '0.875rem',
                            fontWeight: 700,
                            boxShadow: '0 4px 16px rgba(168, 216, 230, 0.4)',
                            zIndex: 10
                          }}>
                            📖 Cover
                          </div>
                          <img 
                            src={status.previewUrls[0]} 
                            alt="Book Cover" 
                            style={{
                              width: '100%',
                              borderRadius: '20px',
                              boxShadow: '0 20px 40px -10px rgba(180, 160, 229, 0.4)',
                              border: '3px solid #e0d5f0',
                            }}
                          />
                        </div>
                      </div>

                      {/* Content Pages */}
                      {status.previewUrls.length > 1 && (
                        <>
                          <h4 style={{ 
                            fontSize: '1.125rem', 
                            fontWeight: 700, 
                            marginBottom: '1rem',
                            textAlign: 'center',
                          }}>
                            🎨 Coloring Pages
                          </h4>
                          <div style={{ 
                            display: 'grid', 
                            gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
                            gap: '1.5rem'
                          }}>
                            {status.previewUrls.slice(1).map((url: string, i: number) => (
                              <div key={i + 1} style={{ 
                                borderRadius: '12px',
                                overflow: 'hidden',
                                boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)'
                              }}>
                                <img src={url} alt={`Page ${i + 2}`} style={{ width: '100%', display: 'block' }} />
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </>
                  )}

                  {status.pdfUrl && (
                    <div style={{ marginTop: '1.5rem', textAlign: 'center' }}>
                      <a 
                        href={status.pdfUrl} 
                        download 
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '0.75rem',
                          padding: '1.25rem 3rem',
                          fontSize: '1.125rem',
                          fontWeight: 700,
                          color: 'white',
                          background: 'linear-gradient(135deg, #A8D8E6 0%, #C9B6E4 50%, #F8C8A0 100%)',
                          border: 'none',
                          borderRadius: '60px',
                          textDecoration: 'none',
                          boxShadow: '0 8px 32px rgba(168, 216, 230, 0.4)',
                          transition: 'all 0.3s ease',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'translateY(-4px) scale(1.02)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'translateY(0) scale(1)';
                        }}
                      >
                        <Download size={22} />
                        <span>Download Your Story Book</span>
                      </a>
                      {status.watermark && (
                        <p style={{ marginTop: '1rem', fontSize: '0.875rem', color: 'var(--color-text-tertiary)' }}>
                          This is a watermarked preview. Generate a full book for the complete version!
                        </p>
                      )}
                    </div>
                  )}
                </>
              )}

              {status.status === 'failed' && (
                <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--color-danger)' }}>
                  <p>❌ Generation failed. Please try again.</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Books Gallery */}
        <BooksGallery session={session} title="Your Magical Coloring Books Library" />
      </div>

      <style jsx>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }

        /* Mobile: Show collapsible header and expandable section, hide desktop inline buttons */
        @media (max-width: 768px) {
          .age-range-mobile-header {
            display: flex !important;
          }

          .age-range-mobile-expanded {
            display: block !important;
          }

          .age-range-desktop-buttons {
            display: none !important;
          }

          /* Compact spacing for mobile */
          .hero-video-section {
            padding: 1rem 0.75rem 0.5rem !important;
          }
          
          .pdf-badge-container {
            margin: 0.75rem auto 0.75rem !important;
            padding: 0 0.75rem !important;
          }
          
          .create-section {
            padding: 0.5rem 0.75rem 1.5rem !important;
          }
          
          /* Hide video overlay on mobile to avoid covering controls */
          .hero-video-overlay {
            display: none !important;
          }

          /* Mobile-optimized PDF badge - Compact version */
          .pdf-badge {
            gap: 0.5rem !important;
            padding: 0.75rem 1rem !important;
          }
          
          /* Hide unnecessary elements on mobile */
          .pdf-badge > div:first-child {
            display: none !important; /* Hide shimmer */
          }
          
          .pdf-badge > div:nth-child(2) {
            width: 32px !important;
            height: 32px !important;
            min-width: 32px;
          }
          
          .pdf-badge > div:nth-child(2) svg {
            width: 18px !important;
            height: 18px !important;
          }
          
          .pdf-badge > div:nth-child(3) > div:first-child {
            font-size: 0.875rem !important;
            margin-bottom: 0 !important;
          }
          
          .pdf-badge > div:nth-child(3) > div:last-child {
            display: none !important; /* Hide subtitle */
          }
          
          .pdf-badge > div:last-child {
            display: none !important; /* Hide printer icon */
          }
        }

        /* Small mobile adjustments */
        @media (max-width: 480px) {
          .pdf-badge {
            padding: 0.625rem 0.875rem !important;
            gap: 0.375rem !important;
          }
          
          .pdf-badge > div:nth-child(3) > div:first-child {
            font-size: 0.8125rem !important;
          }
        }

        /* Desktop: Hide mobile collapsible version, show inline buttons */
        @media (min-width: 769px) {
          .age-range-mobile-header {
            display: none !important;
          }

          .age-range-mobile-expanded {
            display: none !important;
          }

          .age-range-desktop-buttons {
            display: flex !important;
          }
        }
      `}</style>
    </>
  );
}

export default function ColoringPageFull() {
  return (
    <Suspense fallback={<div style={{ minHeight: '100vh', background: 'linear-gradient(180deg, #FFF9F5 0%, #FFF5F0 30%, #FFEFE8 60%, #FFE8E0 100%)' }} />}>
      <ColoringPageContent />
    </Suspense>
  );
}

